import { Asset, getNetworkDisplayName } from '@mls-wallet/shared-types';
import { NetworkBadges } from './shared/network-badges';
import { TokenLogo } from './shared/token-logo';
import { formatUnits } from 'viem';
import { Badge } from './ui/badge';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { formatUsdValue } from '@/lib/utils';

export interface AssetWithUsdValues extends Asset {
  totalBalance: number;
  totalUsdValue: number | null;
  balancesByNetwork: (Asset['balancesByNetwork'][0] & {
    usdValue: number | null;
  })[];
}

interface AssetAccordianProps {
  asset: AssetWithUsdValues;
}

export function AssetAccordian({ asset }: AssetAccordianProps) {
  const formatPlainNumber = (value: number, decimals: number) => {
    if (isNaN(value)) return '--';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const formatTokenBalance = (balance: string, decimals: number) => {
    try {
      const formatted = formatUnits(BigInt(balance), decimals);
      const num = parseFloat(formatted);
      if (isNaN(num)) return '--';
      // Format with commas for thousands separators
      return formatPlainNumber(num, decimals);
    } catch (_error) {
      // Fallback to placeholder text
      return '--';
    }
  };

  const formatNumber = (balance: number, decimals: number) => {
    try {
      return formatPlainNumber(balance, decimals);
    } catch (_error) {
      // Fallback to placeholder text
      return '--';
    }
  };

  return (
    <AccordionItem value={asset.id} className="border rounded-lg bg-background">
      <AccordionTrigger className="px-4 py-3 hover:no-underline">
        <div className="flex w-full justify-between items-center pr-4">
          <div className="grid grid-cols-4 gap-4 w-full text-left">
            <div className="flex items-center gap-2 font-bold text-lg">
              <TokenLogo
                symbol={asset.symbol}
                logoUrl={asset.logoUrl || ''}
                size="sm"
              />
              {asset.symbol}
            </div>
            <div className="flex gap-1">
              <NetworkBadges
                networks={asset.balancesByNetwork.map((b) => b.network)}
                maxVisible={3}
              />
            </div>
            <div className="text-md font-semibold justify-self-end">
              {formatNumber(asset.totalBalance, asset.decimals)}
            </div>
            <div className="text-md font-semibold justify-self-end">
              {formatUsdValue(asset.totalUsdValue)}
            </div>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pl-4 pr-12 pb-3 bg-zinc-50 rounded-b-md border-t border-border">
        <div className="space-y-2 pt-3">
          {asset.balancesByNetwork.map((balance) => {
            return (
              <div
                key={balance.network}
                className="grid grid-cols-4 gap-4 text-sm py-2"
              >
                <div></div>
                <div>
                  <Badge variant="secondary" className="text-xs">
                    {getNetworkDisplayName(balance.network)}
                  </Badge>
                </div>
                <div className="font-semibold justify-self-end">
                  {formatTokenBalance(balance.balance, asset.decimals)}
                </div>
                <div className="font-semibold justify-self-end">
                  {formatUsdValue(balance.usdValue)}
                </div>
              </div>
            );
          })}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
